import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SeoHead from '../components/seo/SeoHead';
import './Login.css';

const WAVE_BARS = 36;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, authState, setAuthState } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    if (authState.user) {
      if (authState.user.email_confirmed_at) {
        navigate('/home', { replace: true });
      } else {
        navigate('/activate', { replace: true });
      }
    }
  }, [authState.user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setAuthState(prev => ({ ...prev, error: null }));
    await signIn(email, password);
    setLoading(false);
  };

  const clearError = () => {
    if (authState.error) {
      setAuthState(prev => ({ ...prev, error: null }));
    }
  };

  return (
    <div className={`login-page ${visible ? 'login-page--visible' : ''}`}>
      <SeoHead
        title="Sign In"
        description="Sign in to DHH Collab — India's Desi Hip-Hop collaboration platform. Access beats, record verses, and connect with producers and rappers."
        path="/login"
      />

      <div className="login-page__bg" aria-hidden>
        <span className="login-page__blob login-page__blob--1" />
        <span className="login-page__blob login-page__blob--2" />
        <span className="login-page__grid" />
      </div>

      <div className="login-page__inner container-padding">
        <div className="login-page__layout">
          <aside className="login-page__visual" aria-hidden>
            <span className="login-page__eyebrow">DHH Collab</span>
            <h1 className="login-page__title">Welcome Back</h1>
            <p className="login-page__lead">
              Sign in to discover beats, record verses, and connect with India&apos;s hip-hop community.
            </p>

            <div className="login-page__graphic">
              <div className="login-page__vinyl" />
              <div className="login-page__orbit">
                <span className="login-page__orbit-dot login-page__orbit-dot--1" />
                <span className="login-page__orbit-dot login-page__orbit-dot--2" />
                <span className="login-page__orbit-dot login-page__orbit-dot--3" />
              </div>
              <div className="login-page__wave">
                {Array.from({ length: WAVE_BARS }).map((_, i) => {
                  const h = 18 + Math.abs(Math.sin(i * 0.55) * 72) + (i % 4) * 4;
                  return (
                    <span
                      key={i}
                      className="login-page__wave-bar"
                      style={
                        {
                          '--bar-i': i,
                          '--bar-height': `${h}%`,
                        } as React.CSSProperties
                      }
                    />
                  );
                })}
              </div>
            </div>
          </aside>

          <div className="login-page__card">
            <header className="login-page__card-head">
              <h2 className="login-page__card-title">Sign in</h2>
              <p className="login-page__card-subtitle">Enter your credentials to continue</p>
            </header>

            <form className="login-page__form" onSubmit={handleSubmit}>
              <div className="login-page__fields">
                <div className="login-page__field">
                  <label htmlFor="email" className="login-page__label">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      clearError();
                    }}
                    className="login-page__input"
                    placeholder="Enter your email"
                  />
                </div>
                <div className="login-page__field">
                  <label htmlFor="password" className="login-page__label">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={e => {
                      setPassword(e.target.value);
                      clearError();
                    }}
                    className="login-page__input"
                    placeholder="Enter your password"
                  />
                </div>
              </div>

              {authState.error && <div className="login-page__error">{authState.error}</div>}

              <button
                type="submit"
                disabled={loading}
                className={`login-page__submit ${loading ? 'login-page__submit--disabled' : 'login-page__submit--ready'}`}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <div className="login-page__links">
                <Link to="/forgot-password" className="login-page__forgot">
                  Forgot your password?
                </Link>
                <p className="login-page__signup">
                  Don&apos;t have an account? <Link to="/signup">Sign up</Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
